import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePlayer } from '../contexts/PlayerContext';
import { Album, Song } from '../types';
import { albumsAPI, songsAPI } from '../services/api';
import StarRating from './StarRating';
import LyricsCard from './LyricsCard';
import LoadingSpinner from './LoadingSpinner';
import styled from 'styled-components';


const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 40px;
  align-items: flex-end;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 20px;
    margin-bottom: 32px;
  }
`;

const AlbumCover = styled.div<{ image?: string }>`
  width: 232px;
  height: 232px;
  background: ${props => props.image 
    ? `url(${props.image})` 
    : 'linear-gradient(45deg, var(--primary-color), var(--secondary-color))'};
  background-size: cover;
  background-position: center;
  border-radius: 16px;
  flex-shrink: 0;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.6);
  position: relative;
  
  @media (max-width: 768px) {
    width: 200px;
    height: 200px;
    border-radius: 20px;
  }
  
  @media (max-width: 480px) {
    width: 180px;
    height: 180px;
    align-self: center;
  }
`;

const AlbumInfo = styled.div`
  flex: 1;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  font-size: 16px;
  
  &:hover {
    color: white;
  }
`;

const AlbumTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 900;
  margin-bottom: 8px;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
    margin-bottom: 12px;
  }
`;

const StatusBadge = styled.div<{ released: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 25px;
  font-weight: 700;
  margin-bottom: 15px;
  position: relative;
  overflow: hidden;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => props.released 
    ? `
      background: linear-gradient(45deg, #10b981, #059669);
      color: white;
      box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
    `
    : `
      background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
      color: white;
      box-shadow: 0 4px 15px rgba(255, 107, 53, 0.3);
    `
  }
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 6px 12px;
  }
`;

const ArtistInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  color: var(--text-muted);
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    justify-content: center;
    margin-bottom: 20px;
  }
`;

const Description = styled.p`
  color: var(--text-muted);
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    justify-content: center;
    gap: 12px;
  }
`;

const PlayButton = styled.button`
  background: #1db954;
  color: white;
  border: none;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 8px 24px rgba(29, 185, 84, 0.3);
  
  &:hover {
    background: #1ed760;
    transform: scale(1.05);
    box-shadow: 0 12px 32px rgba(29, 185, 84, 0.4);
  }
  
  @media (max-width: 768px) {
    &:hover {
      transform: none;
    }
    
    &:active {
      transform: scale(0.95);
    }
  }
`;

const AdminActions = styled.div`
  display: flex;
  gap: 8px;
  
  button {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: var(--text-muted);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
      color: white;
    }
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SongsList = styled.div`
  margin-top: 32px;
`;

const SongsHeader = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr auto;
  gap: 16px;
  padding: 8px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 8px;
  
  span {
    color: var(--text-muted);
    font-size: 0.8rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SongItem = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr auto;
  gap: 16px;
  align-items: center;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
  margin: 0 0 4px 0;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 48px 1fr;
    gap: 12px;
    padding: 12px 16px;
  }
`;

const TrackNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  font-size: 0.9rem;
  font-weight: 500;
  position: relative;
  
  .play-button {
    display: none;
    background: none;
    border: none;
    color: white;
    font-size: 14px;
    cursor: pointer;
  }
  
  ${SongItem}:hover & {
    .track-num {
      display: none;
    }
    .play-button {
      display: block;
    }
  }
`;

const SongInfo = styled.div`
  min-width: 0;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
`;

const SongName = styled.div`
  font-weight: 400;
  font-size: 1rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: white;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const SongMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
  
  @media (max-width: 768px) {
    margin-top: 0;
  }
`;

const SongActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
  
  ${SongItem}:hover & {
    opacity: 1;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SongPlayButton = styled.button`
  background: var(--primary-color);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.2s ease;
  margin-right: 12px;
  
  &:hover {
    transform: scale(1.05);
    background: var(--secondary-color);
  }
`;

const Modal = styled.div<{ show: boolean }>`
  display: ${props => props.show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--dark-bg);
  padding: 30px;
  border-radius: 15px;
  width: 90%;
  max-width: 500px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

const TextArea = styled.textarea`
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  min-height: 150px;
  resize: vertical;
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

const AlbumDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { playSong, pauseSong, currentSong, isPlaying } = usePlayer();
  
  const [album, setAlbum] = useState<Album | null>(null);
  const [songs, setSongs] = useState<Song[]>([]);
  const [showSongModal, setShowSongModal] = useState(false);
  const [editingSong, setEditingSong] = useState<Song | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // Estados do formulário
  const [songName, setSongName] = useState('');
  const [trackNumber, setTrackNumber] = useState('');
  const [songFile, setSongFile] = useState<File | null>(null);
  const [lyrics, setLyrics] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        const [albumResponse, songsResponse] = await Promise.all([
          albumsAPI.getAll(),
          songsAPI.getByAlbum(Number(id))
        ]);
        
        const albumData = albumResponse.data.find(a => a.id === Number(id));
        setAlbum(albumData || null);
        setSongs(songsResponse.data);
      } catch (error) {
        console.error('Erro ao carregar dados do álbum:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      loadData();
    }
  }, [id]);

  const loadAlbumData = async () => {
    try {
      const [albumResponse, songsResponse] = await Promise.all([
        albumsAPI.getAll(),
        songsAPI.getByAlbum(Number(id))
      ]);
      
      const albumData = albumResponse.data.find(a => a.id === Number(id));
      setAlbum(albumData || null);
      setSongs(songsResponse.data);
    } catch (error) {
      console.error('Erro ao carregar dados do álbum:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSong = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploading(true);
    
    const formData = new FormData();
    formData.append('name', songName);
    formData.append('track_number', trackNumber);
    formData.append('lyrics', lyrics);
    if (songFile) {
      formData.append('song', songFile);
    }

    try {
      if (editingSong) {
        await songsAPI.update(editingSong.id, formData);
      } else {
        await songsAPI.create(Number(id), formData);
      }
      
      setShowSongModal(false);
      setSongName('');
      setTrackNumber('');
      setSongFile(null);
      setLyrics('');
      setEditingSong(null);
      loadAlbumData();
    } catch (error) {
      console.error('Erro ao salvar música:', error);
      alert('Erro ao salvar música. Verifique o arquivo e tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  const handlePlaySong = (song: Song) => {
    if (song.file_path) {
      const playableSongs = songs.filter(s => s.file_path).sort((a, b) => a.track_number - b.track_number);
      playSong(song, playableSongs);
    }
  };

  const toggleReleaseStatus = async () => {
    if (album && user?.role === 'admin') {
      try {
        await albumsAPI.updateReleaseStatus(album.id, !album.is_released);
        setAlbum({ ...album, is_released: !album.is_released });
      } catch (error) {
        console.error('Erro ao atualizar status:', error);
      }
    }
  };

  if (loading) {
    return <LoadingSpinner text="Carregando álbum..." />;
  }

  if (!album) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>Álbum não encontrado</div>;
  }

  return (
    <Container>
      <BackButton onClick={() => navigate('/')}>
        ← Voltar
      </BackButton>

      <Header>
        <AlbumCover image={album.cover_image}>
          {/* Overlay para esconder qualquer conteúdo indesejado */}
          <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'transparent',
            pointerEvents: 'none'
          }} />
        </AlbumCover>
        <AlbumInfo>
          <AlbumTitle>{album.name}</AlbumTitle>
          
          <ArtistInfo>
            <span>Soulful Roots</span>
            <span>•</span>
            <span>{songs.length} música{songs.length !== 1 ? 's' : ''}</span>
            {user?.role === 'admin' && (
              <>
                <span>•</span>
                <StatusBadge released={!!album.is_released}>
                  {album.is_released ? 'Lançado' : 'Em Produção'}
                </StatusBadge>
              </>
            )}
          </ArtistInfo>
          
          {album.description && (
            <Description>{album.description}</Description>
          )}
          
          <Actions>
            {songs.filter(s => s.file_path).length > 0 && (
              <PlayButton
                onClick={() => {
                  const playableSongs = songs.filter(s => s.file_path).sort((a, b) => a.track_number - b.track_number);
                  if (playableSongs.length > 0) {
                    playSong(playableSongs[0], playableSongs);
                  }
                }}
                title="Tocar álbum"
              >
                ▶️
              </PlayButton>
            )}
            
            {user?.role === 'admin' && (
              <AdminActions>
                <button 
                  onClick={() => {
                    setEditingSong(null);
                    setSongName('');
                    setTrackNumber('');
                    setSongFile(null);
                    setLyrics('');
                    setShowSongModal(true);
                  }}
                  title="Adicionar Música"
                >
                  ➕
                </button>
                <button 
                  onClick={toggleReleaseStatus}
                  title={album.is_released ? 'Marcar como Produção' : 'Marcar como Lançado'}
                >
                  {album.is_released ? '🔄' : '🚀'}
                </button>
                <button 
                  onClick={async () => {
                    try {
                      await albumsAPI.updateVisibility(album.id, !album.is_hidden);
                      setAlbum({ ...album, is_hidden: !album.is_hidden });
                    } catch (error) {
                      console.error('Erro ao alterar visibilidade:', error);
                    }
                  }}
                  title={album.is_hidden ? 'Mostrar para Ouvintes' : 'Ocultar dos Ouvintes'}
                >
                  {album.is_hidden ? '👁️' : '🙈'}
                </button>
              </AdminActions>
            )}
          </Actions>
        </AlbumInfo>
      </Header>

      <SongsList>
        <SongsHeader>
          <span>#</span>
          <span>Título</span>
          <span>🕒</span>
        </SongsHeader>
        
        {songs.map(song => (
          <SongItem key={song.id}>
            <TrackNumber>
              <span className="track-num">{song.track_number}</span>
              {song.file_path && (
                <button 
                  className="play-button"
                  onClick={() => {
                    if (currentSong?.id === song.id && isPlaying) {
                      pauseSong();
                    } else {
                      handlePlaySong(song);
                    }
                  }}
                >
                  {currentSong?.id === song.id && isPlaying ? '⏸️' : '▶️'}
                </button>
              )}
            </TrackNumber>
            
            <SongInfo>
              <SongName>
                {song.name}
                {song.lyrics && <span style={{ marginLeft: '8px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>🎤</span>}
              </SongName>
              <SongMeta>
                <StarRating songId={song.id} currentRating={song.average_rating} />
                {!song.file_path && <span style={{ color: 'var(--warning-color)', fontSize: '0.8rem' }}>Sem arquivo</span>}
              </SongMeta>
            </SongInfo>
            
            <SongActions>
              {user?.role === 'admin' && (
                <button 
                  onClick={() => {
                    setEditingSong(song);
                    setSongName(song.name);
                    setTrackNumber(song.track_number.toString());
                    setLyrics(song.lyrics || '');
                    setShowSongModal(true);
                  }}
                  title="Editar música"
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: 'var(--text-muted)', 
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px'
                  }}
                >
                  ✏️
                </button>
              )}
            </SongActions>
          </SongItem>
        ))}
        
        {songs.length === 0 && (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
            Nenhuma música adicionada ainda
          </div>
        )}
      </SongsList>

      {/* Card de Letras da Música Atual */}
      {currentSong && songs.find(s => s.id === currentSong.id) && (
        <LyricsCard song={songs.find(s => s.id === currentSong.id)!} />
      )}

      {/* Modal Nova Música */}
      <Modal show={showSongModal}>
        <ModalContent>
          <h2>{editingSong ? 'Editar Música' : 'Nova Música'}</h2>
          <Form onSubmit={handleAddSong}>
            <Input
              type="text"
              placeholder="Nome da música"
              value={songName}
              onChange={(e) => setSongName(e.target.value)}
              required
            />
            <Input
              type="number"
              placeholder="Número da faixa"
              value={trackNumber}
              onChange={(e) => setTrackNumber(e.target.value)}
              required
            />
            <Input
              type="file"
              accept=".mp3,.wav"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const maxSize = 50 * 1024 * 1024; // 50MB
                  if (file.size > maxSize) {
                    alert('Arquivo muito grande! Máximo 50MB.');
                    e.target.value = '';
                    return;
                  }
                }
                setSongFile(file || null);
              }}
            />
            {songFile && (
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                🎧 {songFile.name} ({(songFile.size / 1024 / 1024).toFixed(1)}MB)
              </div>
            )}
            <TextArea
              placeholder="Letra da música (opcional)"
              value={lyrics}
              onChange={(e) => setLyrics(e.target.value)}
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="btn btn-primary" disabled={uploading}>
                {uploading ? '⏳ Salvando...' : (editingSong ? 'Atualizar' : 'Adicionar')}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => {
                  setShowSongModal(false);
                  setEditingSong(null);
                }}
              >
                Cancelar
              </button>
            </div>
          </Form>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default AlbumDetail;